import { Cog, Crosshair, Flame, Zap, Paintbrush, PackageCheck } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { PROCESS_STEPS, MACHINES, IMAGES } from "../data/site";

const ICONS = { Cog, Crosshair, Flame, Zap, Paintbrush, PackageCheck };

export const Manufacturing = () => (
  <section id="technology" data-testid="technology-section" className="bg-brand-ink py-24 sm:py-32 relative overflow-hidden">
    <div className="absolute inset-0 noise-overlay opacity-[0.06] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        dark
        kicker="Manufacturing & Technology"
        title="Precision Engineered. Quality Controlled."
        desc="Every MODORS frame moves through a controlled, machine-led process — from CNC forming to final packing."
      />

      <div className="mt-14 grid md:grid-cols-12 gap-6">
        <Reveal className="md:col-span-7">
          <div className="relative h-72 sm:h-96 overflow-hidden border border-white/10 group">
            <img
              src={IMAGES.cnc}
              alt="CNC press brake forming galvanized steel at MODORS workshop"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-coal/90 to-transparent" />
            <div className="absolute bottom-5 left-5">
              <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-brand-red">Workshop</div>
              <div className="font-display text-xl font-semibold uppercase text-white mt-1">CNC Forming Line</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12} className="md:col-span-5">
          <div className="relative h-72 sm:h-96 overflow-hidden border border-white/10 group">
            <img
              src={IMAGES.welding}
              alt="MIG welding of steel door frames at MODORS factory"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-coal/90 to-transparent" />
            <div className="absolute bottom-5 left-5">
              <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-brand-red">Workshop</div>
              <div className="font-display text-xl font-semibold uppercase text-white mt-1">MIG & Spot Welding</div>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {PROCESS_STEPS.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <Reveal key={s.num} delay={(i % 3) * 0.08} className="bg-brand-ink">
              <div data-testid={`process-step-${i}`} className="group relative h-full p-7 border-t-2 border-brand-red/70">
                <div className="flex items-center justify-between">
                  <span className="font-display text-4xl font-semibold text-white/12 group-hover:text-brand-red/50 transition-colors duration-300">
                    {s.num}
                  </span>
                  <Icon className="w-6 h-6 text-brand-red" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold uppercase tracking-wide text-white">
                  {s.title}
                </h3>
                <p className="mt-2.5 text-sm text-white/50 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
          <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-brand-steel">
            Machines & Processes
          </span>
          {MACHINES.map((m) => (
            <span key={m} className="flex items-center gap-2 text-sm text-white/70">
              <span className="w-1.5 h-1.5 bg-brand-red" /> {m}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
